import DownWhite from '@assets/images/common/icon/arrow/down-white.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { tenantMenuApiClient } from '@src/api/tenant/menus/tenantMenuApiClient';
import { Menu } from '@src/api/types/menu';
import logger from '@src/utils/logger.ts';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface TenantMobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  vw?: number;
}

const TenantMobileSidebar: React.FC<TenantMobileSidebarProps> = ({ isOpen, onClose, vw }) => {
  const [menus, setMenus] = useState<Menu[]>([]);
  const location = useLocation();

  useEffect(() => {
    const fetchMenu = async () => {
      const cachedMenus = localStorage.getItem('tenantMenus');
      if (cachedMenus) {
        setMenus(JSON.parse(cachedMenus));
      }

      const res = await tenantMenuApiClient.index();
      handleApiResponse(res, (data) => {
        setMenus(
          data.map((menu) => ({
            ...menu,
            items: menu.items.map((item) => ({
              ...item,
              isCurrent: location.pathname === item.link,
              items: item.items.map((subItem) => ({
                ...subItem,
                isCurrent: location.pathname === subItem.link,
              })),
            })),
          })),
        );
        localStorage.setItem('tenantMenus', JSON.stringify(data));
      });
      if (res.result !== 'success') {
        logger.error(res.message);
        return;
      }
    };
    fetchMenu();
  }, [location.pathname]);

  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return;
    }

    let startX = 0,
      startY = 0;

    const onStart = (e: TouchEvent): void => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
    };

    const onMove = (e: TouchEvent): void => {
      const t = e.touches[0];
      const dx = Math.abs(t.clientX - startX);
      const dy = Math.abs(t.clientY - startY);
      if (dx > dy) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
    };
  }, [rootRef]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const cls = 'drawer-open-x';
    document.documentElement.classList.add(cls);
    document.body.classList.add(cls);
    return () => {
      document.documentElement.classList.remove(cls);
      document.body.classList.remove(cls);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className='fixed inset-0 z-[10000] flex w-dvw touch-pan-y flex-col overflow-y-auto overflow-x-hidden overscroll-none bg-primary-800 text-white'
      style={{ width: vw ? `${vw}px` : undefined, maxWidth: vw ? `${vw}px` : undefined }}
    >
      <div className='flex h-[60px] items-center justify-end border-b border-gray-400 px-4'>
        <button
          type='button'
          className='grid size-6 place-items-center'
          onClick={onClose}
          aria-label='メニューを閉じる'
        >
          <div className='relative size-4'>
            <span className='absolute left-0 top-1/2 h-[2px] w-4 -translate-y-1/2 rotate-45 bg-white' />
            <span className='absolute left-0 top-1/2 h-[2px] w-4 -translate-y-1/2 -rotate-45 bg-white' />
          </div>
        </button>
      </div>
      <div className='flex-1 overflow-y-auto overflow-x-hidden bg-primary-800 pl-2 pt-5'>
        <ul className='px-4 pb-10'>
          {menus.map((section, sectionIndex) => (
            <li key={sectionIndex}>
              <p
                className={`${sectionIndex === 0 ? 'py-[11px]' : 'pb-[11px] pt-[42px]'} text-sm text-white`}
              >
                {section.title}
              </p>
              <div>
                {section.items.map((item, itemIndex) => {
                  const isOpen =
                    item.items &&
                    item.items.some((dropdownItem) =>
                      location.pathname.startsWith(dropdownItem.link),
                    );
                  if (item.items && item.items.length > 0) {
                    return (
                      <details
                        key={itemIndex}
                        className='group'
                        open={isOpen}
                      >
                        <summary className='flex cursor-pointer items-center gap-4 px-4 py-[11px] text-sm text-white'>
                          <div className='size-[10px]'>
                            <img
                              className='-rotate-90 transition-transform group-open:rotate-0'
                              src={DownWhite}
                              alt=''
                              width='10'
                              height='10'
                            />
                          </div>
                          <div className='select-none text-sm'>{item.title}</div>
                        </summary>
                        <div className='flex flex-col pl-4 text-white'>
                          {item.items.map((dropdownItem, dropdownIndex) => (
                            <Link
                              key={dropdownIndex}
                              to={dropdownItem.link}
                              onClick={onClose}
                              className={`px-2 py-[11px] text-sm text-white transition-colors ${dropdownItem.isCurrent ? 'bg-primary-700' : ''} hover:bg-primary-700`}
                            >
                              ・ {dropdownItem.title}
                            </Link>
                          ))}
                        </div>
                      </details>
                    );
                  } else {
                    return (
                      <div
                        key={itemIndex}
                        className='flex flex-col gap-4 text-white'
                      >
                        <Link
                          to={item.link}
                          onClick={onClose}
                          className={`px-6 py-[11px] text-sm text-white transition-colors ${item.isCurrent ? 'bg-primary-700' : ''} hover:bg-primary-700`}
                        >
                          ・ {item.title}
                        </Link>
                      </div>
                    );
                  }
                })}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default TenantMobileSidebar;
